import { useState } from "react";
import { useDeleteUserData, useGetUserDetails, useGetUserRole } from "../../../../api/userDetails";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { UserRoleOption, UserRow } from "../../shared/types/user.types";
import { UserListViewModel } from "../types/userListView.types";

const DEFAULT_PAGE_SIZE = 10;
const LISTED_STATUS = "ACTIVE";

/**
 * Owns every piece of state/data-fetching the legacy `UserList` kept inline
 * (src/screens/user/userList/UserList.tsx): ACTIVE-only server-side
 * pagination with a selectable page size, the soft-delete flow ("User
 * inactive successfully" toast + refetch), the role list handed to the
 * Register/Edit/Details drawers, and the modal wiring. Presentation only
 * changed; the list refetches on save/delete success instead of on every close.
 */
export const useUserListViewModel = (): UserListViewModel => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [visibleRegisterModal, setVisibleRegisterModal] = useState(false);
  const [detailsId, setDetailsId] = useState("");
  const [editId, setEditId] = useState("");
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [deleteData, setDeleteData] = useState<UserRow | null>(null);

  const {
    data: usersData,
    isLoading: isRowsLoading,
    refetch
  } = useGetUserDetails({ status: LISTED_STATUS, pageNumber, pageSize });
  const { isLoading: isDeleting, mutate: deleteUser } = useDeleteUserData();
  const { data: rolesData } = useGetUserRole();

  const total = usersData?.total || 0;

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const closeRegisterModal = () => setVisibleRegisterModal(false);
  const onSuccessRegister = () => {
    setVisibleRegisterModal(false);
    refetch();
  };

  const openDetailsModal = (row: UserRow) => {
    setDetailsId(row._id);
    setVisibleDetailsModal(true);
  };
  const closeDetailsModal = () => setVisibleDetailsModal(false);
  const navigateDetailsToEdit = () => {
    setEditId(detailsId);
    setVisibleDetailsModal(false);
    setVisibleEditModal(true);
  };

  const openEditModal = (row: UserRow) => {
    setEditId(row._id);
    setVisibleEditModal(true);
  };
  const closeEditModal = () => setVisibleEditModal(false);
  const onSuccessEdit = () => {
    setVisibleEditModal(false);
    refetch();
  };

  const openDeleteConfirm = (row: UserRow) => {
    setDeleteData(row);
    setVisibleDeleteConfirm(true);
  };
  const closeDeleteConfirm = () => {
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };
  const confirmDelete = () => {
    const target = deleteData;
    closeDeleteConfirm();
    if (!target?._id) return;
    deleteUser(target._id, {
      onSuccess: () => {
        successMessage("User inactive successfully");
        refetch();
      }
    });
  };

  return {
    rows: (usersData?.result as unknown as UserRow[]) || [],
    roles: (rolesData as unknown as UserRoleOption[]) || [],
    isRowsLoading,
    totalCount: total,
    totalPages: Math.ceil(total / pageSize) || 1,
    pageNumber,
    pageSize,
    handlePagination: setPageNumber,
    handlePageSize,

    visibleRegisterModal,
    openRegisterModal: () => setVisibleRegisterModal(true),
    closeRegisterModal,
    onSuccessRegister,

    detailsId,
    editId,
    visibleDetailsModal,
    visibleEditModal,
    openDetailsModal,
    closeDetailsModal,
    navigateDetailsToEdit,
    openEditModal,
    closeEditModal,
    onSuccessEdit,

    visibleDeleteConfirm,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,
    isDeleting
  };
};
